import {useRef, useState} from 'react';
import Button from '../../../../components/button';
import Modal from '../../../../components/mazraeKit/modal';
import AutoOtp from '../../../auth/AutoMationSms/AutoOtp';
import {
  PaymentMethodValues,
  paymentTypeMap,
  TransactionTypesChoices,
} from '../../../shopping-basket/cash-payment/utils/helpers';
import {usePostCheckOtpCodeOrders} from './hooks/usePostCheckOtpCodeOrders';
import {usePostCreateCreditTransaction} from './hooks/usePostCreateCreditTransaction';
import OtpInput from './OTPInput';

const OTP_LENGTH = 5;

interface IProps {
  transactionType: TransactionTypesChoices;
  paymentType?: PaymentMethodValues;
  orderGuid?: string;
  storeOrderGuid?: string;
  onClose: () => void;
  onInvalidCode: () => void;
  onInsufficientBalance: () => void;
  onSuccess: () => void;
}

const OtpStep = ({
  transactionType,
  paymentType,
  orderGuid,
  storeOrderGuid,
  onClose,
  onInvalidCode,
  onInsufficientBalance,
  onSuccess,
}: IProps) => {
  const [otp, setOtp] = useState('');

  // Guards against double submits (OtpInput + AutoOtp + button can all fire)
  const isSubmittingRef = useRef(false);

  const resetSubmission = () => {
    isSubmittingRef.current = false;
    setOtp('');
  };

  const handleOtpError = (status: number | null) => {
    resetSubmission();

    if (status === 400) return onInvalidCode();
    if (status === 402) return onInsufficientBalance();

    onClose();
  };

  const {
    mutate: createCreditTransaction,
    isLoading: isCreateTransactionLoading,
  } = usePostCreateCreditTransaction({
    transactionType,
    paymentType,
    orderGuid,
    storeOrderGuid,
    enabled: true,
    onSuccess,
    onError: () => {
      resetSubmission();
      onClose();
    },
  });

  const {
    mutate: sendOtpCode,
    isLoading: isSendOtpLoading,
    hasValidConfig,
  } = usePostCheckOtpCodeOrders({
    transactionType,
    paymentType: paymentType ? paymentTypeMap[paymentType] : undefined,
    orderGuid,
    storeOrderGuid,
    enabled: true,
    handleOnError: handleOtpError,
    handleAfterSuccess: (status: number | null) => {
      if (status === 200) {
        createCreditTransaction();
      } else {
        resetSubmission();
      }
    },
  });

  const isBusy = isSendOtpLoading || isCreateTransactionLoading;
  const isOtpValid = otp.length === OTP_LENGTH;

  const submitOtp = (code: string) => {
    if (!hasValidConfig || code.length !== OTP_LENGTH) return;
    if (isSubmittingRef.current || isBusy) return;

    isSubmittingRef.current = true;
    sendOtpCode(code);
  };

  const handleOtpChange = (value: string) => {
    setOtp(value);
    submitOtp(value);
  };

  return (
    <Modal isOpen setIsOpen={() => onClose()}>
      <div className="w-[320px] flex flex-col p-3 gap-2">
        <div className="flex items-center justify-between">
          <AutoOtp
            setOtp={handleOtpChange}
            length={OTP_LENGTH}
            onAutoSubmit={() => submitOtp(otp)}
          />

          <button onClick={onClose} className="absolute left-4 top-4">
            <i className="text-3xl text-gray-700 icon-nameClose-remove" />
          </button>
        </div>

        <div className="flex flex-col items-center justify-center gap-1">
          <i className="text-4xl icon-chat" />
          <h5 className="mt-1 font-medium text-md text-zinc-800">
            کد اعتبارسنجی
          </h5>
        </div>

        <span className="py-2 text-center text-md">
          کد اعتبارسنجی ارسال شده را وارد کنید
        </span>

        <div className="w-full text-center text-gray-700 text-md">
          <OtpInput
            disabled={isBusy}
            value={otp}
            valueLength={OTP_LENGTH}
            onChange={handleOtpChange}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            className="w-full !bg-[#518A15] h-10"
            disabled={!isOtpValid || isBusy}
            isLoading={isBusy}
            onClick={() => submitOtp(otp)}
          >
            تایید
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default OtpStep;
